// 算分引擎（纯函数，无 Vue 依赖，便于单测）
//
// 依据《企业级数据贯通评价方案-三方十类-v2.0.2.docx》第 5 章：
//   5.3 指标评分：1-5 连续档位，允许半分
//   5.4 能力域得分：域内各指标得分之和 ÷ 域内指标数
//   5.4 综合得分：D = Σ(能力域得分 P_k × 能力域权重 f_k)，D ∈ [1,5]
//       百分制：D% = D / 5 × 100
//   5.5 木桶原理：综合等级不得高于最低能力域所处等级；
//       关键能力域（数据安全与合规、特定领域与场景）处于 1 档时，综合等级封顶 L1
//   5.6 等级映射：L0(D<1) L1(1≤D<2) L2(2≤D<3) L3(3≤D<4) L4(4≤D<5) L5(D=5)
//
// 补充约定（见 data/levels.js）：增设 0 档「未起步」后 D ∈ [0,5]，L0 方可达。
// 文档表 2 的档位区间下界为 30%，即低于 30% 不属于任何 1-5 档，对应新增的 0 档。

import {
  DOMAINS, DEFAULT_WEIGHTS, LEVELS, DOMAIN_ADVICE, SELF_LEVELS, SELF_LEVEL_LABEL
} from '../data/index.js'

export const ANSWER_YES = 'yes'
export const ANSWER_NO = 'no'
export const ANSWER_UNKNOWN = 'unknown'

export const ANSWER_OPTIONS = [
  { value: ANSWER_YES, label: '是', type: 'success' },
  { value: ANSWER_NO, label: '否', type: 'danger' },
  { value: ANSWER_UNKNOWN, label: '不确定', type: 'info' }
]

const LEVEL_INDEX = Object.fromEntries(LEVELS.map((l, i) => [l.level, i]))

export function levelIndex (level) {
  return LEVEL_INDEX[level] == null ? -1 : LEVEL_INDEX[level]
}

export function minLevel (a, b) {
  if (!a) return b
  if (!b) return a
  return LEVEL_INDEX[a] <= LEVEL_INDEX[b] ? a : b
}

/**
 * 依据判定问题勾选结果，推荐一个档位（文档表 2 标尺 + 0 档补充）
 * @param {Object} indicator 指标对象
 * @param {Object} answers   { [问题序号]: 'yes' | 'no' | 'unknown' }
 * @returns {Number|null} 推荐档位；全部未作答时返回 null
 */
export function suggestLevel (indicator, answers) {
  const total = indicator.questions.length
  if (!total) return null

  let answered = 0
  let scored = 0
  for (let i = 0; i < total; i++) {
    const a = answers && answers[i]
    if (!a) continue
    answered++
    if (a === ANSWER_YES) scored += 1
    else if (a === ANSWER_UNKNOWN) scored += 0.5
  }
  if (!answered) return null

  const r = scored / answered
  // 区间与文档表 2 一致；<30% 落入新增的 0 档（文档表 2 未覆盖该区间）
  if (r < 0.3) return 0
  if (r < 0.5) return 1
  if (r < 0.7) return 2
  if (r < 0.85) return 3
  if (r < 0.95) return 4
  return 5
}

/**
 * 按能力域汇总得分
 * @param {Array}  indicators 指标数组
 * @param {Object} scores     { [indicatorId]: { level: Number } }
 * @returns {Array} [{ id, name, weight, total, rated, avg, level }]
 */
export function computeDomainScores (indicators, scores) {
  return DOMAINS.map((domain) => {
    const list = indicators.filter((i) => i.domain === domain.id)
    const rated = list.filter((i) => scores[i.id] && typeof scores[i.id].level === 'number')
    const sum = rated.reduce((s, i) => s + scores[i.id].level, 0)
    const avg = rated.length ? sum / rated.length : null
    return {
      id: domain.id,
      name: domain.name,
      shortName: domain.shortName,
      weight: domain.weight,
      critical: domain.critical,
      total: list.length,
      rated: rated.length,
      avg: avg,
      level: avg == null ? null : scoreToLevel(avg)
    }
  })
}

/**
 * 综合得分（对已评能力域做权重归一化，支持部分评价时给出有意义的中间值）
 * @returns {Number|null}
 */
export function computeComposite (domainResults, weights) {
  let sum = 0
  let wsum = 0
  for (const r of domainResults) {
    if (r.avg == null) continue
    const w = (weights && weights[r.id]) != null ? weights[r.id] : DEFAULT_WEIGHTS[r.id]
    sum += r.avg * w
    wsum += w
  }
  return wsum > 0 ? sum / wsum : null
}

/**
 * 1-5（含 0 档后为 0-5）连续分 → L0..L5
 * 加权求和会产生浮点误差（如全 5 分算出 4.999999999999999），
 * 比较前先收敛到 1e-6，否则满分会被误判为 L4。
 */
export function scoreToLevel (score) {
  if (score == null) return null
  const v = Math.round(score * 1e6) / 1e6
  for (const l of LEVELS) {
    if (v >= l.min && v < l.max) return l.level
  }
  return LEVELS[LEVELS.length - 1].level
}

/**
 * 木桶原理：综合等级不得高于最低能力域等级；关键能力域处于 1 档及以下时封顶 L1
 * @returns {{ finalLevel, bucketCapped, criticalCapped, weakest }}
 */
export function applyBucket (rawLevel, domainResults) {
  if (!rawLevel) return { finalLevel: null, bucketCapped: false, criticalCapped: false, weakest: null }

  const rated = domainResults.filter((r) => r.level)
  if (!rated.length) return { finalLevel: rawLevel, bucketCapped: false, criticalCapped: false, weakest: null }

  let weakest = rated[0]
  for (const r of rated) {
    if (levelIndex(r.level) < levelIndex(weakest.level)) weakest = r
  }

  const criticalCapped = rated.some((r) => r.critical && r.avg < 2)
  let finalLevel = minLevel(rawLevel, weakest.level)
  if (criticalCapped) finalLevel = minLevel(finalLevel, 'L1')

  return {
    finalLevel,
    bucketCapped: finalLevel !== rawLevel,
    criticalCapped,
    weakest
  }
}

/** 找出低于综合原始等级的能力域（短板），按由低到高排序 */
export function findWeakDomains (domainResults, rawLevel) {
  const base = levelIndex(rawLevel)
  return domainResults
    .filter((r) => r.level && levelIndex(r.level) < base)
    .sort((a, b) => levelIndex(a.level) - levelIndex(b.level))
}

/**
 * 一次性算分：能力域得分 → 综合得分 → 木桶约束 → 等级与建议
 * @param {{indicators, scores, weights}} params
 */
export function evaluate ({ indicators, scores = {}, weights = DEFAULT_WEIGHTS } = {}) {
  const domainResults = computeDomainScores(indicators, scores)
  // 收敛到 2 位小数再定级，避免 4.999999999999999 这类浮点误差被判为 L4
  const raw = computeComposite(domainResults, weights)
  const composite = raw == null ? null : Math.round(raw * 100) / 100
  const rawLevel = scoreToLevel(composite)
  const bucket = applyBucket(rawLevel, domainResults)
  const weakDomains = findWeakDomains(domainResults, rawLevel)

  const total = indicators.length
  const rated = indicators.filter((i) => scores[i.id] && typeof scores[i.id].level === 'number').length

  return {
    domainResults,
    composite,
    percent: composite == null ? null : Math.round((composite / 5) * 1000) / 10,
    rawLevel,
    finalLevel: bucket.finalLevel,
    bucketCapped: bucket.bucketCapped,
    criticalCapped: bucket.criticalCapped,
    weakest: bucket.weakest,
    weakDomains,
    advice: weakDomains.map((d) => ({ id: d.id, name: d.name, text: DOMAIN_ADVICE[d.id] })),
    progress: { rated, total, finished: total > 0 && rated === total }
  }
}

/** 能力域权重归一化（项目级权重可调后保证和为 1） */
export function normalizeWeights (weights) {
  const safe = {}
  let sum = 0
  for (const d of DOMAINS) {
    const v = Number(weights && weights[d.id])
    const val = Number.isFinite(v) && v >= 0 ? v : 0
    safe[d.id] = val
    sum += val
  }
  if (sum <= 0) return { ...DEFAULT_WEIGHTS }
  for (const d of DOMAINS) safe[d.id] = Math.round((safe[d.id] / sum) * 1000) / 1000
  return safe
}

/* ------------------------------------------------------------------ *
 * 自评等级（沿用 v1.0.0 定性评价，含亚级）及其到 L0-L5 的归并映射
 * 自评结果仅作对照，不参与任何算分
 * ------------------------------------------------------------------ */

/**
 * 归一化用户输入的自评码，容错多种写法：
 *   'L2a' / 'l2a' / 'LV2-a' / 'Lv2 A' / ' L2b ' → 'L2a' / 'L2b'
 *   'L2'  → 'L2'（不带亚级时按主级处理，定位图上不高亮具体亚级气泡）
 * @param {String} input
 * @returns {String} 规范码；无法识别时返回 ''
 */
export function normalizeSelfCode (input) {
  if (!input) return ''
  const s = String(input)
    .trim()
    .toUpperCase()
    .replace(/^LV/, 'L')
    .replace(/[\s\-_]/g, '')
  const m = /^L([0-5])([AB])?$/.exec(s)
  if (!m) return ''
  return 'L' + m[1] + (m[2] ? m[2].toLowerCase() : '')
}

/**
 * 自评码 → 归并后的 L0-L5（决策 S2：亚级归并到主级）
 * @param {String} code 规范码（未归一化时内部会自动归一）
 * @returns {String|null} 如 'L3'；无法识别返回 null
 */
export function mapSelfLevel (code) {
  const norm = normalizeSelfCode(code)
  if (!norm) return null
  const hit = SELF_LEVELS.find((x) => x.code === norm)
  if (hit) return hit.mapped
  // 'L2' / 'L3' 这类不带亚级的写法：直接取主级
  const bare = /^L([0-5])$/.exec(norm)
  return bare ? 'L' + bare[1] : null
}

/**
 * 自评与实评的偏差分析（决策 S4：对照 + 偏差提示）
 * @param {String} selfCode  自评规范码，如 'L2a'
 * @param {String} finalLevel 实评等级，如 'L3'
 * @returns {Object|null} 无法判定时返回 null
 *   { selfCode, selfLabel, selfLevel, finalLevel, diff, judgement }
 *   diff > 0：实评高于自评（自评偏保守）；diff < 0：自评偏乐观
 */
export function selfLevelDeviation (selfCode, finalLevel) {
  const selfLevel = mapSelfLevel(selfCode)
  if (!selfLevel || !finalLevel) return null
  const i = levelIndex(selfLevel)
  const j = levelIndex(finalLevel)
  if (i < 0 || j < 0) return null

  const diff = j - i
  const gap = Math.abs(diff)
  let judgement
  if (diff === 0) {
    judgement = '自评与实评一致'
  } else if (diff > 0) {
    judgement = gap === 1 ? '自评比实评低 1 级（自评偏保守）'
      : `自评比实评低 ${gap} 级（自评明显偏保守）`
  } else {
    judgement = gap === 1 ? '自评比实评高 1 级（自评偏乐观）'
      : `自评比实评高 ${gap} 级（自评明显偏乐观）`
  }

  return {
    selfCode: normalizeSelfCode(selfCode),
    selfLabel: SELF_LEVEL_LABEL[normalizeSelfCode(selfCode)] || normalizeSelfCode(selfCode),
    selfLevel,
    finalLevel,
    diff,
    judgement
  }
}
